import { useSuspenseQuery } from "@tanstack/react-query";
import { Flex, PrimarySecondaryPage } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";

import { ApplicationPageStepper, DocumentUploadSupportingContent } from "../../../components";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";
import { useDocumentQuery } from "../../../queries/document-uploads/queries";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import { DocumentNavigationGroup, ManagementAccountDocumentUpload } from "../components";

export const ManagementAccountsDocumentUploadPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: uploadedDocuments } = useDocumentQuery(customerDomain, fundingApplicationData.id);
  const hasUploadedDocuments = !!uploadedDocuments?.filter(
    (doc) => doc.documentType === UploadDocumentType.MANAGEMENT_ACCOUNTS
  ).length;

  const navigationContext = useFundingApplicationNavigation({
    currentPage: PAGE_TYPE.DOCUMENTS,
    currentStep: UploadDocumentType.MANAGEMENT_ACCOUNTS,
  });

  const requiredDuration =
    fundingApplicationData.requestedAmount &&
    fundingApplicationData.requestedAmountCurrency &&
    fundingApplicationData.requestedAmount >=
      4 * getAccountingThreshold(businessModel)[fundingApplicationData.requestedAmountCurrency]
      ? 24
      : 12;

  const PrimaryContent = (
    <Flex direction="column" gap="8">
      <ManagementAccountDocumentUpload duration={requiredDuration} />
      <DocumentNavigationGroup isStepComplete={hasUploadedDocuments} forType={UploadDocumentType.MANAGEMENT_ACCOUNTS} />
    </Flex>
  );

  return (
    <PrimarySecondaryPage
      titleSlot={<ApplicationPageStepper currentStepIndex={1} />}
      showStepperBackground={true}
      title={intl.formatMessage({
        id: "funding-application: upload management accounts",
        defaultMessage: "Upload your management accounts",
      })}
      primaryContent={PrimaryContent}
      secondaryContent={<DocumentUploadSupportingContent />}
      titleKicker={navigationContext.stepString}
    />
  );
};
